import { RUN_ENERGY_MAX, type MapData } from "@dogdell/shared";
import { iconSvg } from "./icons";
import { bakeMapTerrain } from "./mapTerrain";

export interface MinimapDot {
  x: number;
  y: number;
  /** Local pup - drawn as the center white dot. */
  local?: boolean;
  /** Other players vs NPCs (RS yellow). */
  kind?: "player" | "npc";
  /** Optional fur tint for remote player dots. */
  color?: number;
}

/**
 * Camera-aligned square minimap: local terrain under the pup,
 * rotates with camera yaw so screen-forward matches map-up.
 */
export class Minimap {
  readonly root: HTMLDivElement;
  readonly runButton: HTMLButtonElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly energyPctEl: HTMLSpanElement;
  /** Full-zone tile bake (1px per tile). */
  private terrain: HTMLCanvasElement | null = null;
  private mapW = 1;
  private mapH = 1;
  private readonly cssSize: number;
  /** How many tiles from center to the edge. */
  private readonly radiusTiles = 16;
  private readonly cornerRadius = 14;
  private lastEnergyShown = -1;

  constructor(parent: HTMLElement, cssSize = 152) {
    this.cssSize = cssSize;
    this.root = document.createElement("div");
    this.root.className = "minimap";
    this.root.setAttribute("aria-label", "Minimap");

    this.canvas = document.createElement("canvas");
    this.canvas.className = "minimap-canvas";
    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Minimap needs a 2D canvas context");
    }
    this.ctx = ctx;
    this.resizeCanvas();

    this.runButton = document.createElement("button");
    this.runButton.type = "button";
    this.runButton.id = "btn-run";
    this.runButton.className = "run-orb";
    this.runButton.title = "Run (Shift)";
    this.runButton.setAttribute("aria-label", "Run");
    this.runButton.setAttribute("aria-pressed", "false");
    this.energyPctEl = document.createElement("span");
    this.energyPctEl.className = "run-orb-pct";
    this.energyPctEl.textContent = "100%";
    const core = document.createElement("span");
    core.className = "run-orb-core";
    core.innerHTML = iconSvg("run", 26);
    core.append(this.energyPctEl);
    this.runButton.append(core);

    this.root.append(this.canvas, this.runButton);
    parent.appendChild(this.root);
    this.setEnergy(RUN_ENERGY_MAX);
  }

  setRunActive(active: boolean): void {
    this.runButton.classList.toggle("is-active", active);
    this.runButton.setAttribute("aria-pressed", String(active));
  }

  setEnergy(energy: number): void {
    const pct = Math.max(0, Math.min(100, Math.round(energy)));
    if (pct === this.lastEnergyShown) {
      return;
    }
    this.lastEnergyShown = pct;
    this.energyPctEl.textContent = `${pct}%`;
    this.runButton.style.background = `conic-gradient(#c4783a ${pct * 3.6}deg, rgba(80,56,32,0.28) 0)`;
    this.runButton.classList.toggle("is-empty", pct <= 0);
  }

  private resizeCanvas(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const px = Math.round(this.cssSize * dpr);
    this.canvas.width = px;
    this.canvas.height = px;
    this.canvas.style.width = `${this.cssSize}px`;
    this.canvas.style.height = `${this.cssSize}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Rebuild the static terrain layer when the zone map changes. */
  setMap(map: MapData): void {
    this.mapW = map.width;
    this.mapH = map.height;
    this.terrain = bakeMapTerrain(map);
  }

  /**
   * @param localX local pup pose X (map tiles)
   * @param localY local pup pose Y (map tiles)
   * @param yaw ParkView camera yaw - map rotates so view-forward is up
   * @param waypoint optional click-to-move destination in map tiles
   */
  draw(
    localX: number,
    localY: number,
    yaw: number,
    dots: readonly MinimapDot[],
    waypoint?: { x: number; y: number } | null,
  ): void {
    const { ctx, cssSize: size, radiusTiles, cornerRadius } = this;
    const cx = size / 2;
    const cy = size / 2;
    const scale = size / (radiusTiles * 2);
    const pad = 3;
    // Camera at +yaw from focus; look direction on XZ is −yaw → screen-up.
    const rot = -yaw;

    ctx.clearRect(0, 0, size, size);

    // Rounded-square clip.
    ctx.save();
    roundRectPath(ctx, pad, pad, size - pad * 2, size - pad * 2, cornerRadius);
    ctx.clip();

    ctx.fillStyle = "#b9a57a";
    ctx.fillRect(0, 0, size, size);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    ctx.imageSmoothingEnabled = false;

    if (this.terrain) {
      ctx.drawImage(
        this.terrain,
        0,
        0,
        this.mapW,
        this.mapH,
        -localX * scale,
        -localY * scale,
        this.mapW * scale,
        this.mapH * scale,
      );
    }

    for (const dot of dots) {
      if (dot.local) {
        continue;
      }
      const dx = (dot.x - localX) * scale;
      const dy = (dot.y - localY) * scale;
      const isNpc = dot.kind === "npc";
      const fill = isNpc
        ? "#ffe082"
        : dot.color !== undefined
          ? cssHex(dot.color)
          : "#ffffff";
      const r = isNpc ? 2.6 : 2.8;
      ctx.beginPath();
      ctx.fillStyle = "#1a1208";
      ctx.arc(dx, dy, r + 1.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.fillStyle = fill;
      ctx.arc(dx, dy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Waypoint pin - position follows map rotation, art stays screen-upright.
    if (waypoint) {
      const mx = (waypoint.x - localX) * scale;
      const my = (waypoint.y - localY) * scale;
      const cos = Math.cos(rot);
      const sin = Math.sin(rot);
      drawWaypointPin(ctx, cx + mx * cos - my * sin, cy + mx * sin + my * cos);
    }

    // Local player - white center dot (screen-fixed; map rotates under it).
    ctx.save();
    ctx.translate(cx, cy);
    ctx.beginPath();
    ctx.fillStyle = "#1a1208";
    ctx.arc(0, 0, 4.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = "#ffffff";
    ctx.arc(0, 0, 3.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.restore(); // end clip

    // Wood/stone frame.
    ctx.lineJoin = "round";
    roundRectPath(ctx, pad, pad, size - pad * 2, size - pad * 2, cornerRadius);
    ctx.strokeStyle = "#3e2f1c";
    ctx.lineWidth = 4;
    ctx.stroke();
    roundRectPath(ctx, pad + 2, pad + 2, size - (pad + 2) * 2, size - (pad + 2) * 2, cornerRadius - 2);
    ctx.strokeStyle = "#d4b888";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // N sits in the top-right corner of the minimap (screen-fixed).
    const nX = size - pad - 12;
    const nY = pad + 12;
    ctx.fillStyle = "rgba(26, 18, 8, 0.55)";
    ctx.beginPath();
    ctx.arc(nX, nY, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ff5252";
    ctx.font = "bold 11px Nunito, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("N", nX, nY + 0.5);
  }

  destroy(): void {
    this.root.remove();
    this.terrain = null;
  }
}

function drawWaypointPin(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.save();
  ctx.translate(x, y);
  // Soft ground ring
  ctx.beginPath();
  ctx.fillStyle = "rgba(255, 245, 157, 0.45)";
  ctx.arc(0, 0, 5.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.strokeStyle = "rgba(58, 40, 24, 0.7)";
  ctx.lineWidth = 1.2;
  ctx.arc(0, 0, 5.5, 0, Math.PI * 2);
  ctx.stroke();
  // Pole
  ctx.strokeStyle = "#3e2723";
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, -11);
  ctx.stroke();
  // Flag
  ctx.fillStyle = "#fff3d4";
  ctx.strokeStyle = "#3e2723";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, -11);
  ctx.lineTo(9, -8);
  ctx.lineTo(0, -5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function cssHex(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}
